/* =========================================================================
 * /docs/what — sensor reference.
 *
 * Rewritten as part of the 2026 redesign. The previous version used the
 * legacy React Native sensor schemas (dBFS, altitudeAccuracy, etc.) and an
 * iOS-vs-Android split that no longer applies. Field names and units below
 * match what the current Android app actually uploads.
 * ========================================================================= */

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import SiteFooter from "components/SiteFooter";

import consumerRoutes from "consumerRoutes";
import footerRoutes from "footer.routes";

// ---------------------------------------------------------------------------
// Sensor catalog — fields + sample reading per sensor. Shapes match the
// actual Android FirestoreUploader output (single-reading shape; in the wire
// format these are batched inside a readings[] array per epoch).
// ---------------------------------------------------------------------------

const SENSORS = [
  {
    icon: "location_on",
    title: "Location",
    body: "GPS coordinates with altitude, speed, bearing, and accuracy radius. Sampled at 1 Hz; drives the epoch boundary for all other sensors.",
    fields: [
      ["latitude", "Degrees (-90 to 90)"],
      ["longitude", "Degrees (-180 to 180)"],
      ["altitude", "Meters above WGS-84 ellipsoid (nullable)"],
      ["accuracyMeters", "Radius of uncertainty (nullable)"],
      ["speedMps", "Instantaneous speed in m/s (nullable)"],
      ["bearingDegrees", "Direction of travel, 0 = north (nullable)"],
      ["timestamp", "ms since epoch"],
    ],
    sample: {
      latitude: -34.6037,
      longitude: -58.3816,
      altitude: 25.4,
      accuracyMeters: 5,
      speedMps: 1.8,
      bearingDegrees: 150,
      timestamp: 1780163080000,
    },
  },
  {
    icon: "vibration",
    title: "Accelerometer",
    body: "Three-axis device acceleration including gravity (m/s²). Used for motion analysis and shake detection.",
    fields: [
      ["x, y, z", "Acceleration along each axis, m/s²"],
      ["t", "Reading timestamp, ms since epoch"],
    ],
    sample: { x: 0.12, y: -9.78, z: 0.34, t: 1780163080000 },
  },
  {
    icon: "rotate_right",
    title: "Gyroscope",
    body: "Three-axis angular velocity in radians per second. Useful for rotation, orientation, and transport-mode classification.",
    fields: [
      ["x, y, z", "Rotation rate around each axis, rad/s"],
      ["t", "Reading timestamp, ms since epoch"],
    ],
    sample: { x: 0.02, y: -0.01, z: 0.005, t: 1780163080000 },
  },
  {
    icon: "explore",
    title: "Magnetometer",
    body: "Magnetic field strength in microteslas (μT) along three axes. Combined with the gyroscope, supports orientation inference.",
    fields: [
      ["x, y, z", "Field strength along each axis, μT"],
      ["t", "Reading timestamp, ms since epoch"],
    ],
    sample: { x: 22.4, y: -3.1, z: -41.6, t: 1780163080000 },
  },
  {
    icon: "3d_rotation",
    title: "Device motion (α/β/γ)",
    body: "Device orientation expressed as rotation around the Z, X, and Y axes (alpha, beta, gamma) — the same triplet web browsers expose.",
    fields: [
      ["x", "Alpha — rotation around Z axis, degrees"],
      ["y", "Beta — rotation around X axis, degrees"],
      ["z", "Gamma — rotation around Y axis, degrees"],
      ["t", "Reading timestamp, ms since epoch"],
    ],
    sample: { x: 184.2, y: 12.5, z: -3.8, t: 1780163080000 },
  },
  {
    icon: "navigation",
    title: "Compass",
    body: "Heading in degrees from magnetic north (0 = north, 90 = east, 180 = south, 270 = west).",
    fields: [
      ["value", "Heading, 0–359 degrees"],
      ["t", "Reading timestamp, ms since epoch"],
    ],
    sample: { value: 142.7, t: 1780163080000 },
  },
  {
    icon: "speed",
    title: "Barometer",
    body: "Atmospheric pressure in hectopascals (hPa). Useful for weather, storm tracking, microclimate analysis, and altitude inference.",
    fields: [
      ["value", "Pressure in hPa (typical range 950–1050)"],
      ["t", "Reading timestamp, ms since epoch"],
    ],
    sample: { value: 1013.25, t: 1780163080000 },
  },
  {
    icon: "light_mode",
    title: "Light",
    body: "Ambient illuminance in lux. Bright daylight ≈ 10,000+ lx; office ≈ 500 lx; moonlit night ≈ 1 lx.",
    fields: [
      ["value", "Illuminance in lux"],
      ["t", "Reading timestamp, ms since epoch"],
    ],
    sample: { value: 8200.5, t: 1780163080000 },
  },
  {
    icon: "graphic_eq",
    title: "Microphone (amplitude only)",
    body: "Normalized sound level (0.0 = silent, 1.0 = max). Strictly amplitude — no audio is ever captured or transmitted.",
    privacy: "Audio content stays on the device.",
    fields: [
      ["value", "Normalized amplitude, 0.0–1.0"],
      ["t", "Reading timestamp, ms since epoch"],
    ],
    sample: { value: 0.18, t: 1780163080000 },
  },
  {
    icon: "signal_cellular_alt",
    title: "Carrier",
    body: "Cellular network info — carrier name, network type, and signal strength. Useful for connectivity and rural-coverage studies.",
    fields: [
      ["carrierName", "Operator name string"],
      ["networkType", "e.g. LTE / 5G / WCDMA / Unknown"],
      ["signalStrengthDbm", "Signal strength in dBm (nullable)"],
      ["t", "Reading timestamp, ms since epoch"],
    ],
    sample: {
      carrierName: "Movistar",
      networkType: "LTE",
      signalStrengthDbm: -78,
      t: 1780163080000,
    },
  },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const WhatData = () => {
  return (
    <>
      <DefaultNavbar routes={consumerRoutes} sticky />

      {/* Hero */}
      <MKBox
        component="section"
        sx={({ palette }) => ({
          background: `linear-gradient(180deg, ${palette.white.main} 0%, #EFF6FF 100%)`,
          pt: { xs: 12, md: 16 },
          pb: { xs: 8, md: 10 },
        })}
      >
        <Container>
          <Grid container justifyContent="center" textAlign="center">
            <Grid item xs={12} md={10} lg={9}>
              <MKTypography
                variant="overline"
                color="primary"
                fontWeight="bold"
                sx={{ letterSpacing: "0.1em" }}
              >
                Sensor reference
              </MKTypography>
              <MKTypography
                variant="h1"
                color="dark"
                mt={1.5}
                sx={({ breakpoints, typography: { size } }) => ({
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                  [breakpoints.down("md")]: { fontSize: size["3xl"] },
                })}
              >
                What data we collect
              </MKTypography>
              <MKTypography
                variant="body1"
                color="text"
                mt={3}
                sx={{ maxWidth: 720, mx: "auto", fontSize: { xs: 16, md: 18 }, lineHeight: 1.55 }}
              >
                Every sensor the Bripplex app reads, the shape of each reading, and the units.
                Shapes here are individual samples — in the wire format they&apos;re batched inside
                a <code>readings[]</code> array per 20-second epoch.
              </MKTypography>
            </Grid>
          </Grid>
        </Container>
      </MKBox>

      {/* Sensors */}
      <MKBox component="section" py={{ xs: 8, md: 12 }} px={{ xs: 2, md: 4 }}>
        <Container maxWidth="md">
          {SENSORS.map((s, i) => (
            <MKBox
              key={s.title}
              id={s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}
              sx={({ palette }) => ({
                py: { xs: 4, md: 5 },
                borderTop: i === 0 ? "none" : `1px solid ${palette.grey[200]}`,
              })}
            >
              <Grid container spacing={{ xs: 2, md: 4 }} alignItems="flex-start">
                {/* Header column */}
                <Grid item xs={12} md={4}>
                  <MKBox display="flex" alignItems="center" sx={{ gap: 2, mb: 2 }}>
                    <MKBox
                      sx={({ palette }) => ({
                        width: 44,
                        height: 44,
                        borderRadius: "10px",
                        display: "grid",
                        placeItems: "center",
                        backgroundColor: "rgba(37, 99, 255, 0.1)",
                        color: palette.primary.main,
                        flexShrink: 0,
                      })}
                    >
                      <Icon fontSize="small">{s.icon}</Icon>
                    </MKBox>
                    <MKTypography
                      variant="h5"
                      color="dark"
                      fontWeight="bold"
                      sx={{ letterSpacing: "-0.01em" }}
                    >
                      {s.title}
                    </MKTypography>
                  </MKBox>
                  <MKTypography variant="body2" color="text" sx={{ lineHeight: 1.6 }}>
                    {s.body}
                  </MKTypography>
                  {s.privacy && (
                    <MKBox
                      sx={({ palette }) => ({
                        mt: 2,
                        p: 1.5,
                        borderRadius: 1.5,
                        background: "rgba(20, 184, 166, 0.08)",
                        border: `1px solid ${palette.secondary.main}`,
                      })}
                    >
                      <MKTypography
                        variant="caption"
                        sx={({ palette }) => ({
                          color: palette.secondary.focus,
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        })}
                      >
                        <Icon fontSize="inherit">lock</Icon> {s.privacy}
                      </MKTypography>
                    </MKBox>
                  )}
                </Grid>

                {/* Right column — fields + JSON */}
                <Grid item xs={12} md={8}>
                  <MKTypography
                    variant="overline"
                    color="primary"
                    fontWeight="bold"
                    sx={{ letterSpacing: "0.1em" }}
                  >
                    Fields
                  </MKTypography>
                  <MKBox component="dl" sx={{ m: 0, mt: 1, mb: 2.5 }}>
                    {s.fields.map(([name, desc]) => (
                      <MKBox
                        key={name}
                        sx={{
                          display: { xs: "block", sm: "grid" },
                          gridTemplateColumns: "180px 1fr",
                          py: 0.75,
                          borderBottom: ({ palette }) => `1px dashed ${palette.grey[200]}`,
                          gap: 2,
                        }}
                      >
                        <MKTypography
                          component="dt"
                          variant="body2"
                          sx={({ palette }) => ({
                            fontFamily: "monospace",
                            color: palette.primary.main,
                            fontWeight: 600,
                          })}
                        >
                          {name}
                        </MKTypography>
                        <MKTypography component="dd" variant="body2" color="text" sx={{ m: 0 }}>
                          {desc}
                        </MKTypography>
                      </MKBox>
                    ))}
                  </MKBox>
                  <MKTypography
                    variant="overline"
                    color="primary"
                    fontWeight="bold"
                    sx={{ letterSpacing: "0.1em" }}
                  >
                    Sample reading
                  </MKTypography>
                  <MKBox
                    sx={({ borders }) => ({
                      mt: 1,
                      borderRadius: borders.borderRadius.md,
                      overflow: "hidden",
                      "& pre": {
                        margin: "0 !important",
                        padding: "14px 16px !important",
                        fontSize: "12.5px !important",
                        background: "#0F172A !important",
                      },
                    })}
                  >
                    <SyntaxHighlighter language="json" style={oneDark}>
                      {JSON.stringify(s.sample, null, 2)}
                    </SyntaxHighlighter>
                  </MKBox>
                </Grid>
              </Grid>
            </MKBox>
          ))}
        </Container>
      </MKBox>

      {/* Aggregation callout */}
      <MKBox
        component="section"
        py={{ xs: 6, md: 8 }}
        px={{ xs: 2, md: 4 }}
        sx={({ palette }) => ({
          background: palette.grey[100],
          borderTop: `1px solid ${palette.grey[200]}`,
        })}
      >
        <Container maxWidth="md">
          <MKTypography variant="body1" color="dark" sx={{ lineHeight: 1.65 }}>
            All sensor readings are grouped under the device&apos;s current{" "}
            <MKBox component="strong" sx={{ color: "primary.main" }}>
              location epoch
            </MKBox>{" "}
            (a 20-sample window). The epoch is the unit of upload and the unit of aggregation. For
            the full pipeline see{" "}
            <MKBox
              component="a"
              href="/docs/how"
              sx={({ palette }) => ({
                color: palette.primary.main,
                textDecoration: "underline",
                fontWeight: 600,
              })}
            >
              How we collect the data
            </MKBox>
            .
          </MKTypography>
        </Container>
      </MKBox>

      <SiteFooter content={footerRoutes} />
    </>
  );
};
